// =====================================================
// Transaction Review Card Component
// Single transaction review with inline edit
// =====================================================

import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Check, Pencil, X, Upload, AlertTriangle, FileText, BookOpen } from 'lucide-react';
import { formatSwissCurrency } from '@/integrations/supabase/client';
import type { ReviewTransaction, VATWarning } from '@/services/reviewQueueService';
import { ReceiptUploader, ReceiptList, ReceiptViewer } from '@/components/receipts';
import { receiptService, type ReceiptUpload } from '@/services/receiptService';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { createBookingFromTransaction } from '@/services/bookingService';
// Removed feature flags and metrics tracking to avoid missing modules


interface TransactionReviewCardProps {
  transaction: ReviewTransaction;
  vatWarning?: VATWarning | null;
  isSelected?: boolean;
  onApprove: () => void;
  onEdit: (updates: { debit_account?: string; credit_account?: string; vat_rate?: number }) => void;
  onReject: () => void;
  onUploadReceipt?: () => void;
  onToggleSelect?: () => void;
}

export function TransactionReviewCard({
  transaction,
  vatWarning,
  isSelected,
  onApprove,
  onEdit,
  onReject,
  onUploadReceipt,
  onToggleSelect,
}: TransactionReviewCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedDebitAccount, setEditedDebitAccount] = useState(transaction.debit_account || '');
  const [editedCreditAccount, setEditedCreditAccount] = useState(transaction.credit_account || '');
  const [editedVATRate, setEditedVATRate] = useState(transaction.vat_rate?.toString() || '0');
  
  // Receipt management state
  const [receipts, setReceipts] = useState<ReceiptUpload[]>([]);
  const [selectedReceipt, setSelectedReceipt] = useState<ReceiptUpload | null>(null);
  const [showReceiptUploader, setShowReceiptUploader] = useState(false);
  const [isLoadingReceipts, setIsLoadingReceipts] = useState(false);
  
  // Booking integration state
  const [isCreatingBooking, setIsCreatingBooking] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    loadReceipts();
  }, [transaction.id]);

  const loadReceipts = async () => {
    setIsLoadingReceipts(true);
    try {
      const data = await receiptService.getTransactionReceipts(transaction.id);
      setReceipts(data);
    } catch (error) {
      console.error('Error loading receipts:', error);
    } finally {
      setIsLoadingReceipts(false);
    }
  };

  const confidence = transaction.classification_confidence || 0;
  const isUncertain = confidence < 80;

  const handleSaveEdit = async () => {
    try {
      // Record correction for learning
      const correctionData = {
        transactionId: transaction.id,
        correctedDebitAccount: editedDebitAccount,
        correctedCreditAccount: editedCreditAccount,
        correctedVatCode: editedVATRate,
        correctionReason: 'User manual correction'
      };

      // Call the record-correction Edge Function
      const response = await fetch(`${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/record-correction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify(correctionData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Correction recorded and pattern learned:', result);
        // Show success message
        toast.success('Correction saved and learned!');
      } else {
        console.warn('Failed to record correction for learning:', await response.text());
        // Still proceed with the edit even if learning fails
        toast.success('Correction saved (learning may not be recorded)');
      }
    } catch (error) {
      console.error('Error recording correction:', error);
      // Still proceed with the edit even if learning fails
      toast.success('Correction saved (learning may not be recorded)');
    }

    // Update the transaction
    onEdit({
      debit_account: editedDebitAccount,
      credit_account: editedCreditAccount,
      vat_rate: parseFloat(editedVATRate),
    });
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditedDebitAccount(transaction.debit_account || '');
    setEditedCreditAccount(transaction.credit_account || '');
    setEditedVATRate(transaction.vat_rate?.toString() || '0');
    setIsEditing(false);
  };

  // Quick fix VAT warning
  const handleQuickFixVAT = () => {
    if (vatWarning) {
      setEditedVATRate(vatWarning.expectedVAT.toString());
      setIsEditing(true);
    }
  };

  // Create booking entry via typed RPC helper
  const handleCreateBooking = async () => {
    try {
      // Check if booking already exists
      if (transaction.journal_entry_id) {
        toast({ 
          title: "Booking already exists", 
          description: "This transaction has already been booked.",
          variant: "default" 
        });
        return;
      }
      
      setIsCreatingBooking(true);
      
      const result = await createBookingFromTransaction(transaction.id);

      await queryClient.invalidateQueries({ queryKey: ['transactions'] });
      await queryClient.invalidateQueries({ queryKey: ['bookings'] });
      await queryClient.invalidateQueries({ queryKey: ['journal-entries'] });

      // Track successful booking
      

      toast({
        title: "✅ Journal Entry Created!",
        description: `Entry #${result.entry_number} successfully booked`,
        variant: "default",
      });

      queryClient.invalidateQueries({ queryKey: ['review-queue'] });
    } catch (error: any) {
      console.error('Booking creation failed:', error);
      
      toast({ 
        title: "❌ Booking failed", 
        description: error.message || 'An error occurred while creating the booking.',
        variant: "destructive" 
      });
    } finally {
      setIsCreatingBooking(false);
    }
  };

  return (
    <Card className={`relative ${isSelected ? 'ring-2 ring-brand-primary' : ''}`}>
      {/* Confidence indicator on left border */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 rounded-l ${
          confidence >= 90
            ? 'bg-green-500'
            : confidence >= 70
            ? 'bg-yellow-500'
            : 'bg-red-500'
        }`}
      />

      <CardContent className="p-4 pl-6">
        {/* Header Row */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            {/* Checkbox for bulk selection */}
            {onToggleSelect && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={onToggleSelect}
                className="mr-3 align-middle"
              />
            )}

            {/* Date */}
            <span className="text-sm text-gray-600 mr-3">
              {new Date(transaction.date).toLocaleDateString('de-CH')}
            </span>

            {/* Counterparty */}
            <span className="font-semibold text-brand-primary">
              {transaction.counterparty}
            </span>

            {/* Amount */}
            <span
              className={`ml-3 font-semibold ${
                transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {transaction.amount >= 0 ? '+' : '-'} {formatSwissCurrency(Math.abs(transaction.amount))}
            </span>
          </div>

          {/* Confidence Badge */}
          <Badge
            variant={confidence >= 90 ? 'default' : confidence >= 70 ? 'secondary' : 'destructive'}
            className="ml-2"
          >
            {confidence}% {transaction.classification_source || 'AI'}
          </Badge>
        </div>

        {/* VAT Warning */}
        {vatWarning && !isEditing && (
          <Alert variant="destructive" className="mb-3 py-2">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span className="text-sm">
                {vatWarning.reason} - Expected {vatWarning.expectedVAT}% VAT, currently {vatWarning.currentVAT}%
              </span>
              <Button size="sm" variant="outline" onClick={handleQuickFixVAT}>
                Quick Fix
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Uncertain AI Badge */}
        {isUncertain && !isEditing && (
          <Alert className="mb-3 py-2 bg-yellow-50 border-yellow-200">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-sm text-yellow-800">
              AI is uncertain about this classification ({confidence}%)
            </AlertDescription>
          </Alert>
        )}

        {/* Classification Details or Edit Mode */}
        {isEditing ? (
          <div className="space-y-3 mb-3 bg-gray-50 p-3 rounded">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="debit-account" className="text-xs">
                  Debit Account (Soll)
                </Label>
                <Input
                  id="debit-account"
                  value={editedDebitAccount}
                  onChange={(e) => setEditedDebitAccount(e.target.value)}
                  placeholder="e.g., 6400"
                  className="h-8"
                />
              </div>
              <div>
                <Label htmlFor="credit-account" className="text-xs">
                  Credit Account (Haben)
                </Label>
                <Input
                  id="credit-account"
                  value={editedCreditAccount}
                  onChange={(e) => setEditedCreditAccount(e.target.value)}
                  placeholder="e.g., 1020"
                  className="h-8"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="vat-rate" className="text-xs">
                VAT Rate (%)
              </Label>
              <select
                id="vat-rate"
                value={editedVATRate}
                onChange={(e) => setEditedVATRate(e.target.value)}
                className="w-full h-8 px-3 border rounded text-sm"
              >
                <option value="0">0% (Export/Exempt)</option>
                <option value="2.6">2.6% (Accommodation)</option>
                <option value="3.8">3.8% (Tourism)</option>
                <option value="8.1">8.1% (Standard)</option>
              </select>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSaveEdit} className="flex-1">
                <Check className="h-4 w-4 mr-1" />
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancelEdit} className="flex-1">
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-700 space-y-1 mb-3">
            <div>
              <span className="font-medium">Debit:</span> {transaction.debit_account || 'Uncategorized'} |{' '}
              <span className="font-medium">Credit:</span> {transaction.credit_account || 'Uncategorized'}
            </div>
            <div>
              <span className="font-medium">VAT:</span> {transaction.vat_rate || 0}%
              {transaction.vat_code && <span className="ml-2 text-gray-500">({transaction.vat_code})</span>}
            </div>
            {transaction.description && (
              <div className="text-gray-600 text-xs">{transaction.description}</div>
            )}
          </div>
        )}

        {/* Receipts Section */}
        <div className="mb-3 border-t border-gray-200 pt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-brand-primary" />
              <h4 className="text-sm font-semibold text-brand-primary">Receipts</h4>
              {receipts.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {receipts.length}
                </Badge>
              )}
              {receipts.some(r => r.has_mismatches) && (
                <Badge variant="destructive" className="text-xs">
                  Issues
                </Badge>
              )}
            </div>
            {receipts.length > 0 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowReceiptUploader(!showReceiptUploader)}
                className="text-xs"
              >
                {showReceiptUploader ? 'Hide Upload' : 'Add Receipt'}
              </Button>
            )}
          </div>

          {/* Show uploader if no receipts or user clicked Add Receipt */}
          {(receipts.length === 0 || showReceiptUploader) && (
            <ReceiptUploader
              transactionId={transaction.id}
              mandateId={transaction.mandate_id}
              onUploadComplete={() => {
                loadReceipts();
                setShowReceiptUploader(false);
              }}
              maxFiles={3}
              className="mb-3"
            />
          )}

          {/* Show receipt list if we have receipts */}
          {receipts.length > 0 && (
            <ReceiptList
              transactionId={transaction.id}
              onReceiptClick={setSelectedReceipt}
            />
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="default"
            onClick={onApprove}
            disabled={isEditing}
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            <Check className="h-4 w-4 mr-1" />
            Approve (A)
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleCreateBooking}
            disabled={isCreatingBooking || isEditing || Boolean(transaction.journal_entry_id)}
            className="flex-1"
            title={
              transaction.journal_entry_id
                ? 'Already booked'
                : 'Create journal entry for this transaction'
            }
          >
            <BookOpen className="h-4 w-4 mr-1" />
            {transaction.journal_entry_id
              ? 'Booked'
              : isCreatingBooking
              ? 'Creating...'
              : 'Book'}
          </Button>
          
          <Button
            size="sm"
            variant="outline"
            onClick={() => setIsEditing(!isEditing)}
            className="flex-1"
          >
            <Pencil className="h-4 w-4 mr-1" />
            Edit (E)
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={onReject}
            disabled={isEditing}
            title="Reject (R)"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Receipt Viewer Modal */}
        <ReceiptViewer
          receipt={selectedReceipt}
          open={!!selectedReceipt}
          onClose={() => setSelectedReceipt(null)}
          onDelete={() => {
            loadReceipts();
            setSelectedReceipt(null);
          }}
        />
      </CardContent>
    </Card>
  );
}
